import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import {
  loadCollectJsScript,
  normalizePayrocTokenizationKey,
} from "../config/payroc.ts";
import ServiceApiService from "../services/service.service";
import type { ServiceConfirmationData } from "./models";

declare global {
  interface Window {
    CollectJS: {
      configure: (config: Record<string, unknown>) => void;
      startPaymentRequest: () => void;
    };
  }
}

const ServiceConfirmation = () => {
  const { id } = useParams<{ id: string }>();
  const [status, setStatus] = useState<string | null>(null);
  const [serviceData, setServiceData] = useState<ServiceConfirmationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const tokenizationKey = normalizePayrocTokenizationKey(
    serviceData?.tokenizationKey,
  );

  const getDepositValue = (rawAmount?: string | number) => {
    const parsed = Number(rawAmount);
    if (Number.isNaN(parsed)) {
      return 0;
    }
    return parsed;
  };

  const getApiError = (err: unknown) => {
    if (typeof err !== "object" || err === null) {
      return null;
    }
    return err as {
      response?: {
        status?: number;
        data?: {
          errors?: {
            nonFieldErrors?: string[];
          };
        };
      };
    };
  };

  const extractPaymentToken = (payload: unknown) => {
    if (typeof payload === "string") {
      return payload;
    }

    if (typeof payload === "object" && payload !== null) {
      const collectPayload = payload as {
        token?: string;
        payment_token?: string;
      };

      return collectPayload.token || collectPayload.payment_token || null;
    }

    return null;
  };

  const confirm = useCallback(async (token?: string | null) => {
    if (!id) {
      setError("not found");
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = token ? { token } : {};
      const response = await ServiceApiService.confirmServicePayment(id, payload);
      if (response.status === 200) {
        const depositValue = getDepositValue(serviceData?.depositAmount);
        setStatus(depositValue > 0 ? "paid" : "confirmed");
      }
    } catch (err) {
      setStatus("error");
      const apiError = getApiError(err);
      if (apiError?.response?.status === 400) {
        setError(apiError.response.data?.errors?.nonFieldErrors?.[0] ?? "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [id, serviceData?.depositAmount]);

  const configureCollectJs = useCallback(
    (depositValue: number) => {
      if (!tokenizationKey) {
        setStatus("error");
        setError("Payment is not configured for this account.");
        return;
      }

      if (!window.CollectJS) {
        setStatus("error");
        setError("Payment form failed to load. Please refresh and try again.");
        return;
      }

      window.CollectJS.configure({
        variant: "lightbox",
        buttonText: `Pay $${depositValue.toFixed(2)} to confirm`,
        instructionText: "Enter Card Info Below",
        price: depositValue.toFixed(2),
        currency: "USD",
        country: "US",
        callback: (payload: unknown) => {
          const paymentToken = extractPaymentToken(payload);
          if (!paymentToken) {
            setStatus("error");
            setError("Payment token was not returned. Please try again.");
            setIsSubmitting(false);
            return;
          }

          setIsSubmitting(true);
          confirm(paymentToken);
        },
      });
    },
    [confirm, tokenizationKey],
  );

  useEffect(() => {
    const fetchService = async () => {
      if (!id) {
        setError("not found");
        return;
      }

      try {
        const response = await ServiceApiService.getServiceConfirmation(id);
        if (response.status === 200) {
          setStatus("nonpaid");
          setServiceData(response.data.data);
        }
      } catch (err) {
        const apiError = getApiError(err);
        if (apiError?.response?.status === 400) {
          setError(apiError.response.data?.errors?.nonFieldErrors?.[0] ?? "error");
        }
      }
    };

    fetchService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const depositValue = getDepositValue(serviceData?.depositAmount);
    if (depositValue <= 0) {
      return;
    }

    if (window.CollectJS) {
      configureCollectJs(depositValue);
      return;
    }

    if (!tokenizationKey) {
      return;
    }

    return loadCollectJsScript(tokenizationKey, () =>
      configureCollectJs(depositValue),
    );
  }, [configureCollectJs, serviceData, tokenizationKey]);

  return (
    <div className="col-md-12">
      <div className="card card-container text-center p-4">
        <img
          src="https://lirp.cdn-website.com/a85f75f8/dms3rep/multi/opt/LayoutsDB67747D-C218-0AC4-C1B8-2184CDD25E0F.png_400-356w.png"
          alt="logo"
          width={200}
          className="mx-auto mb-3"
        />
        {error ? (
          <div className="alert alert-danger">{error}</div>
        ) : status === "nonpaid" && serviceData ? (
          <>
            {(() => {
              const depositValue = getDepositValue(serviceData.depositAmount);
              const requiresPayment = depositValue > 0;

              return (
                <>
            <h4>Dear {serviceData.serviceClient}</h4>
            <p>
              Your service appointment is set to:
              <br />
              <strong>{moment(serviceData.serviceDate).format("LLL")}</strong>
            </p>
            {requiresPayment && (
              <p>
                Deposit required: <strong>${depositValue.toFixed(2)}</strong>
              </p>
            )}
            <Button
              variant="danger"
              onClick={() => {
                setIsSubmitting(true);
                if (requiresPayment) {
                  if (!tokenizationKey) {
                    setStatus("error");
                    setError("Payment is not configured for this account.");
                    setIsSubmitting(false);
                    return;
                  }

                  if (!window.CollectJS) {
                    setStatus("error");
                    setError("Payment form failed to load. Please refresh and try again.");
                    setIsSubmitting(false);
                    return;
                  }

                  window.CollectJS.startPaymentRequest();
                } else {
                  confirm();
                }
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                (requiresPayment
                  ? `Pay $${depositValue.toFixed(2)} to Confirm`
                  : "Confirm Appointment")
              )}
            </Button>
                </>
              );
            })()}
          </>
        ) : status === "paid" || status === "confirmed" ? (
          <>
            <h4>Thank you, {serviceData?.serviceClient}!</h4>
            <p>
              Your service appointment is confirmed for{" "}
              <strong>{moment(serviceData?.serviceDate).format("LLL")}</strong>
            </p>
          </>
        ) : (
          <Spinner animation="border" className="mx-auto" />
        )}
      </div>
    </div>
  );
};

export default ServiceConfirmation;
